import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { Router, RouterModule } from '@angular/router';
import { DelabNgxPageTitleComponent } from '@delab/ngx-ixconnect-components';
import { Licenses } from '@shared/constants';
import { License } from '@shared/types';
import { map, Observable } from 'rxjs';
import { EventTrackerNavigationReasons } from 'src/app/shared/constants';
import { ConfigService } from '../../services/config.service';
import { LicensesService } from '../../services/licenses.service';
import { PageCardNewComponent } from './page-card/page-card-new.component';
import { PageCardButton } from './types/PageCardButton.types';

interface StartPageCard {
    iconName: string;
    title: string;
    content: string;
    requiredLicenses?: License[];
    buttons: PageCardButton[];
}

const CARDS_PER_ROW = 2;

@Component({
    selector: 'app-start-page-cards',
    templateUrl: './start-page-cards.component.html',
    styleUrls: ['./start-page-cards.component.scss'],
    imports: [
        // Angular
        CommonModule,
        RouterModule,
        // Angular Material
        MatButtonModule,
        // Own
        PageCardNewComponent,
        DelabNgxPageTitleComponent,
    ],
})
export class StartPageCardsComponent {
    public readonly DEFAULT_CARD_CLASSES: string = `absolute max-w-[400px] h-full`;

    protected EventTrackerNavigationReasons = EventTrackerNavigationReasons;

    private readonly allCards: StartPageCard[] = [
        {
            iconName: 'assignment',
            title: 'Aufträge',
            content:
                'Sie können neue Aufträge erstellen, den Status angelegter Aufträge einsehen und Dokumente zu Aufträgen herunterladen oder ausdrucken.',
            requiredLicenses: [Licenses.Orders, Licenses.OrderCreation],
            buttons: [
                { text: 'Auftrag erstellen', route: '/order-creation', requiredLicense: Licenses.OrderCreation },
                { text: 'Auftragsübersicht', route: '/orders', requiredLicense: Licenses.Orders },
            ],
        },
        {
            iconName: 'assignment_turned_in',
            title: 'Befunde',
            content: 'Hier können Sie Befunde ansehen und herunterladen, ältere Befunde suchen und exportieren.',
            requiredLicenses: [Licenses.Reports],
            buttons: [{ text: 'Befundübersicht', route: '/reports', requiredLicense: Licenses.Reports }],
        },
        {
            iconName: 'folder',
            title: 'Dokumente',
            content: 'Hier können Sie Dokumente mit dem Labor austauschen und Patientendokumente einsehen.',
            requiredLicenses: [Licenses.Documents],
            buttons: [
                { text: 'Praxisdokumente', route: '/view-documents', requiredLicense: Licenses.Documents },
                { text: 'Patientendokumente', route: '/patients-search', requiredLicense: Licenses.PatientsSearch },
            ],
        },
        {
            iconName: 'shopping_cart',
            title: 'Shop',
            content: 'Hier können Sie kostenlos Probenmaterial bestellen und sich den Status Ihrer Bestellungen ansehen.',
            requiredLicenses: [Licenses.Shop],
            buttons: [
                { text: 'Neue Bestellung', route: '/shop/article-selection', requiredLicense: Licenses.Shop },
                { text: 'Bestellübersicht', route: '/shop/order-overview', requiredLicense: Licenses.Shop },
            ],
        },
    ];

    public readonly visibleCards$: Observable<StartPageCard[]> = this._licenseService.licensesObs.pipe(
        map((licenses) =>
            this.allCards
                .filter((card) => {
                    if (!card.requiredLicenses || card.requiredLicenses.length === 0) {
                        return true;
                    }

                    return card.requiredLicenses.some((license) => licenses.has(license));
                })
                .map((card) => ({
                    ...card,
                    buttons: card.buttons.filter((button) => {
                        if (button === 'placeholderButton') {
                            return false;
                        }

                        return !button.requiredLicense || licenses.has(button.requiredLicense);
                    }),
                }))
                .filter((card) => card.buttons.length > 0),
        ),
    );

    public readonly cardRows$: Observable<StartPageCard[][]> = this.visibleCards$.pipe(
        map((cards) => {
            const rows: StartPageCard[][] = [];
            for (let i = 0; i < cards.length; i += CARDS_PER_ROW) {
                rows.push(cards.slice(i, i + CARDS_PER_ROW));
            }
            return rows;
        }),
    );

    public readonly welcomeText$: Observable<string | undefined> = this._configService.configObs.pipe(
        map((config) => config?.ixservConfig?.landingPageWelcomeText?.trim() || undefined),
    );

    constructor(
        private readonly _licenseService: LicensesService,
        private readonly _configService: ConfigService,
        private readonly _router: Router,
    ) {}

    onCardButtonClicked(button: PageCardButton) {
        if (button === 'placeholderButton') {
            return;
        }

        this._router.navigate([button.route]);
    }
}
